// Market Watch Utility Functions

export type HtmlBadgeType = "critical" | "warning" | "info" | "good";

export interface HtmlBadge {
  text: string;
  type: HtmlBadgeType | null;
}

export interface HtmlListItem {
  text: string;
  badge?: HtmlBadge | null;
}

export interface HtmlMetricCard {
  value: string;
  label: string;
  badge?: HtmlBadge | null;
}

export interface HtmlSection {
  title: string;
  paragraphs: string[];
  listItems: HtmlListItem[];
  metrics: HtmlMetricCard[];
}

const parseBadge = (badgeEl: Element | null): HtmlBadge | null => {
  if (!badgeEl) return null;
  const text = badgeEl.textContent?.trim() || "";
  if (!text) return null;
  const classList = badgeEl.classList;
  const type: HtmlBadgeType | null = classList.contains("critical")
    ? "critical"
    : classList.contains("warning")
      ? "warning"
      : classList.contains("info")
        ? "info"
        : classList.contains("good")
          ? "good"
          : null;
  return { text, type };
};

// Parse HTML report into structured sections (LLM HTML classes)
export const parseHtmlIntoSectionsDetailed = (htmlContent: string): HtmlSection[] => {
  if (!htmlContent) return [];

  const sections: HtmlSection[] = [];
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlContent;

  const sectionNodes = tempDiv.querySelectorAll(".insights-section");
  if (sectionNodes.length > 0) {
    sectionNodes.forEach((sectionEl, idx) => {
      const headerEl = sectionEl.querySelector("h1, h2, h3");
      const title = headerEl?.textContent?.trim() || `Section ${idx + 1}`;
      const paragraphs = Array.from(sectionEl.querySelectorAll("p"))
        .map((p) => p.textContent?.trim() || "")
        .filter((t) => t.length > 0);

      const listItems: HtmlListItem[] = [];
      sectionEl.querySelectorAll("ul.insights-list li").forEach((li) => {
        const text =
          li.querySelector(".item-text")?.textContent?.trim() || li.textContent?.trim() || "";
        if (!text) return;
        const badge = parseBadge(li.querySelector(".metric-badge"));
        listItems.push({ text, badge });
      });

      const metrics: HtmlMetricCard[] = [];
      // Capture metrics from .metrics-grid AND standalone .metric-card elements
      sectionEl.querySelectorAll(".metric-card").forEach((card) => {
        const value = card.querySelector(".metric-value")?.textContent?.trim() || "";
        const label = card.querySelector(".metric-label")?.textContent?.trim() || "";
        if (!value && !label) return;
        const badge = parseBadge(card.querySelector(".metric-badge"));
        metrics.push({ value, label, badge });
      });

      sections.push({ title, paragraphs, listItems, metrics });
    });

    return sections;
  }

  const headers = tempDiv.querySelectorAll("h1, h2, h3");

  if (headers.length === 0) {
    const text = tempDiv.textContent?.trim() || htmlContent;
    return [{ title: "Analysis Report", paragraphs: [text], listItems: [], metrics: [] }];
  }

  headers.forEach((header, idx) => {
    const title = header.textContent?.trim() || `Section ${idx + 1}`;
    let content = "";

    let sibling = header.nextElementSibling;
    while (sibling && !["H1", "H2", "H3"].includes(sibling.tagName)) {
      content += sibling.outerHTML || "";
      sibling = sibling.nextElementSibling;
    }

    if (content.trim()) {
      const tempSection = document.createElement("div");
      tempSection.innerHTML = content;
      const paragraphs = Array.from(tempSection.querySelectorAll("p"))
        .map((p) => p.textContent?.trim() || "")
        .filter((t) => t.length > 0);
      if (paragraphs.length === 0) {
        const text = tempSection.textContent?.trim() || content;
        paragraphs.push(text);
      }
      sections.push({ title, paragraphs, listItems: [], metrics: [] });
    }
  });

  return sections.length > 0
    ? sections
    : [{ title: "Analysis Report", paragraphs: [htmlContent], listItems: [], metrics: [] }];
};

// Parse HTML report into sections (legacy, simplified)
export const parseHtmlIntoSections = (
  htmlContent: string
): Array<{ title: string; content: string }> => {
  if (!htmlContent) return [];
  const sections: Array<{ title: string; content: string }> = [];
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlContent;
  const headers = tempDiv.querySelectorAll("h1, h2, h3");
  if (headers.length === 0) {
    return [{ title: "Analysis Report", content: htmlContent }];
  }
  headers.forEach((header, idx) => {
    const title = header.textContent?.trim() || `Section ${idx + 1}`;
    let content = "";
    let sibling = header.nextElementSibling;
    while (sibling && !["H1", "H2", "H3"].includes(sibling.tagName)) {
      content += sibling.outerHTML || "";
      sibling = sibling.nextElementSibling;
    }
    if (content.trim()) {
      sections.push({ title, content });
    }
  });
  return sections.length > 0 ? sections : [{ title: "Analysis Report", content: htmlContent }];
};

// Sanitize cell value for Excel (convert null/undefined/objects to strings)
export const sanitizeValue = (value: any): string | number | boolean => {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") return JSON.stringify(value);
  return value;
};

// Sanitize entire row
export const sanitizeRow = (row: any[]): (string | number | boolean)[] => {
  return row.map(sanitizeValue);
};
